import { useState } from 'react'
import { generatePath } from 'react-router-dom'
import { Badge, CompanyTag } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import {
  ReferralMaterialsCategoriesPageKey,
  ReferralMaterialsUrls,
} from 'api/ClientArea/ReferralMaterialsDto'
import { Link } from 'components/Link'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const ellipsisOverflowStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 'fit-content',
}

function UrlsRow(props: GridCellProps) {
  const [expanded, setExpanded] = useState(false)
  const toggle = () => setExpanded((e) => !e)
  const urls: ReferralMaterialsUrls = props.dataItem.urls || []
  const max = 4

  const visibleItems = expanded ? urls : urls.slice(0, max)

  if (!urls.length) {
    return <td style={{ padding: '0.5rem' }}>-</td>
  }

  return (
    <td style={{ padding: '0.5rem' }}>
      <div style={{ display: 'flex', gap: '1rem' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: '120px',
            ...ellipsisOverflowStyle,
          }}
        >
          {visibleItems.map((item, idx) => (
            <div key={`base-${idx}`}>{item.baseUrlType.name || '-'}</div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', ...ellipsisOverflowStyle }}>
          {visibleItems.map((item, idx) => (
            <div style={ellipsisOverflowStyle} key={`target-${idx}`}>
              {item.targetUrl || '-'}
            </div>
          ))}
        </div>
      </div>

      {urls.length > max && (
        <Button
          variant='link'
          style={{
            marginTop: '0.25rem',
            padding: 0,
          }}
          onClick={toggle}
        >
          {expanded ? 'Hide' : 'Expand'}
        </Button>
      )}
    </td>
  )
}

export const referralMaterialsCategoriesColumn: GridColumn[] = [
  {
    title: 'Category name',
    field: ReferralMaterialsCategoriesPageKey.name,
    cell: (props: GridCellProps) => (
      <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
        <Link
          to={generatePath(links.referralMaterialsCategories.detailsUrl as string, {
            id: props.dataItem.id,
            slug: 'details',
          })}
        >
          {props.dataItem.name}
        </Link>
      </td>
    ),
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'URLs',
    field: ReferralMaterialsCategoriesPageKey.urls,
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
    cell: (props: GridCellProps) => <UrlsRow {...props} />,
  },
  {
    title: 'Entity',
    field: ReferralMaterialsCategoriesPageKey.entity,
    cell: (props: GridCellProps) => {
      const tickmillCompany = props.dataItem.tickmillCompany
      if (!tickmillCompany?.id) return <td />

      return (
        <td style={{ textAlign: 'center' }}>
          <CompanyTag id={tickmillCompany.id}>{tickmillCompany.name}</CompanyTag>
        </td>
      )
    },
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    width: 100,
    minResizableWidth: 80,
  },
  {
    title: 'Active',
    field: ReferralMaterialsCategoriesPageKey.active,
    cell: (props: GridCellProps) => {
      const isActive = props.dataItem.isActive || false
      return (
        <td width={80} style={{ textAlign: 'center' }}>
          <Badge variant={isActive ? 'green' : 'red'}>{isActive ? 'Yes' : 'No'}</Badge>
        </td>
      )
    },
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    width: 100,
    minResizableWidth: 80,
  },
  {
    title: 'End Date',
    field: ReferralMaterialsCategoriesPageKey.endDate,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.endDateTime, true)}
      </td>
    ),
    show: true,
    className: 'table-columns__date',
    filter: 'text',
    width: 125,
  },
  {
    title: 'Last Updated',
    field: ReferralMaterialsCategoriesPageKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    show: true,
    className: 'table-columns__date',
    filter: 'text',
    width: 125,
  },
]
